import React, { useState, useRef } from 'react';
import { Card, List, Button, Tag, Form, Dimmer } from "tabler-react";
import { useFetch } from '../../lib/useFetch';
import PutObject from '../../lib/putObject';
import { toBase64 } from '../../lib/helpers';

const AdminUserEditModal = ({ recordId, closeMainModal, reload }) => {
  const { data: admin, loading, handleChange } = useFetch(`admins/${recordId}`);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const [hasNewImage, setHasNewImage] = useState(false);
  const [removeImage, setRemoveImage] = useState(false);
  const photoFile = useRef(null);

  const validateForm = () => {
    if (!admin?.firstName) {
      setError('Please enter first name');
      return false;
    }
    if (!admin?.lastName) {
      setError('Please enter last name');
      return false;
    }
    if (!admin?.email) {
      setError('Please enter email');
      return false;
    }
    if (!admin?.phone) {
      setError('Please enter phone number');
      return false;
    }
    if (photoFile.current.files.length > 0 && !photoFile.current.files[0].type.includes('image')) {
      setError('Please select a valid image file');
      return false;
    }
    return true;
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file && file.type.includes('image')) {
      const preview = await toBase64(file);
      setImagePreview(preview);
      setHasNewImage(true);
      setRemoveImage(false);
      setError('');
    } else if (file) {
      setError('Please select a valid image file');
      setImagePreview(null);
      setHasNewImage(false);
    }
  };

  const handleDropZoneClick = () => {
    photoFile.current.click();
  };

  const handleRemoveImage = (e) => {
    e.stopPropagation();
    setImagePreview(null);
    setHasNewImage(false);
    setRemoveImage(true);
    photoFile.current.value = '';
    // Force reset the file input
    if (photoFile.current) {
      photoFile.current.type = '';
      photoFile.current.type = 'file';
    }
  };

  const saveEdit = async () => {
    setError('');
    if (!validateForm()) return;

    try {
      setSaveLoading(true);
      
      const editedAdmin = {
        _id: recordId,
        firstName: admin.firstName,
        lastName: admin.lastName,
        email: admin.email,
        phone: admin.phone,
      };

      if (removeImage) {
        editedAdmin.photo = '';
      } else if (hasNewImage && photoFile.current.files.length > 0) {
        editedAdmin.photo = await toBase64(photoFile.current.files[0]);
      }
      
      await PutObject('admins', editedAdmin);
      
      if (typeof reload === 'function') {
        reload();
      }
      closeMainModal();
    } catch (err) {
      setError('Failed to update admin user. Please try again.');
    } finally {
      setSaveLoading(false);
    }
  };

  // Get current image source for preview
  const getCurrentImageSrc = () => {
    if (removeImage) return null;
    if (hasNewImage && imagePreview) {
      return imagePreview;
    }
    return admin?.photo;
  };

  return (
    <Card>
      <Dimmer active={loading} loader={loading}>
        <Card.Header>
          <Card.Title>Edit Admin User</Card.Title>
          <Card.Options>
            <Button size="sm" icon="x" onClick={closeMainModal} />
          </Card.Options>
        </Card.Header>
        {!loading && admin && (
          <>
            <Card.Body>
              {error && <div className="alert alert-danger">{error}</div>}
              <Form onSubmit={(e) => { e.preventDefault(); saveEdit(); }}>
                <Form.Input
                  name='firstName'
                  label='First Name'
                  placeholder='Enter first name'
                  value={admin['firstName'] || ''}
                  onChange={handleChange}
                  required
                />

                <Form.Input
                  name='lastName'
                  label='Last Name'
                  placeholder='Enter last name'
                  value={admin['lastName'] || ''}
                  onChange={handleChange}
                  required
                />

                <Form.Input
                  name='email'
                  label='E-mail'
                  placeholder='Enter email address'
                  type="email"
                  value={admin['email'] || ''}
                  onChange={handleChange}
                  required
                />

                <Form.Input
                  name='phone'
                  label='Phone'
                  placeholder='Enter phone number'
                  type="tel"
                  value={admin['phone'] || ''}
                  onChange={handleChange}
                  required
                />

                <Form.Label>Roles</Form.Label>
                <List.GroupItem>
                  {admin && admin.roles && admin.roles.map((r, i) => (
                    <Tag key={i}>{r}</Tag>
                  ))}
                </List.GroupItem>

                <Form.Group>
                  <Form.Label>Photo</Form.Label>
                  <input 
                    type='file' 
                    ref={photoFile}
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                  
                  {!getCurrentImageSrc() ? (
                    <div className="drop-zone" onClick={handleDropZoneClick}>
                      <div className="drop-zone-icon">
                        <svg 
                          width="48" 
                          height="48" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="#6c757d" 
                          strokeWidth="1.5" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        >
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                          <circle cx="8.5" cy="8.5" r="1.5"/>
                          <polyline points="21,15 16,10 5,21"/>
                        </svg>
                      </div>
                      <div className="drop-zone-text">
                        {removeImage ? 'Click here to upload new photo' : 'Click here to upload admin photo'}
                      </div>
                      <div className="drop-zone-hint">JPG, PNG, GIF supported</div>
                    </div>
                  ) : (
                    <div className="image-preview-container">
                      <img 
                        src={getCurrentImageSrc()} 
                        alt="Admin Preview" 
                        className="image-preview"
                        onClick={handleDropZoneClick}
                      />
                      <button
                        type="button"
                        className="remove-image-btn"
                        onClick={handleRemoveImage}
                      >
                        ×
                      </button>
                      {!hasNewImage && admin?.photo && !removeImage && (
                        <div className="image-note">Current photo shown. Click to change.</div>
                      )}
                      {hasNewImage && (
                        <div className="image-note">New photo selected. Click to change.</div>
                      )}
                    </div>
                  )}
                </Form.Group>
              </Form>
            </Card.Body>
            <Card.Footer>
              <div className="modal-footer">
                <Button
                  color="primary"
                  size="sm"
                  className={`ms-2 ${saveLoading ? 'btn-loading' : ''}`}
                  onClick={saveEdit}
                  disabled={saveLoading}
                >
                  Save Changes
                </Button>
                <Button
                  color="secondary"
                  size="sm"
                  className="ms-2"
                  onClick={closeMainModal}
                  disabled={saveLoading}
                >
                  Cancel
                </Button>
              </div>
            </Card.Footer>
          </>
        )}
      </Dimmer>
    </Card>
  );
};

export default AdminUserEditModal;